import { fontFamily } from "../../helpers/fontFamily";
import { Text, TouchableOpacity, View, Dimensions } from 'react-native';
import React, { useEffect, useState } from 'react';
import { useFocusEffect, useRoute } from '@react-navigation/native';
import { useDispatch } from 'react-redux';
import { checkPushPermissions } from '../../actions/inventoryReportActions';

const actionButtons = ({ isEditing, handleEdit, handleCancel, handleConfirm, handleSaveEdit, revertChange, isCancelButtons, handlePushConfirm, fromPushInventory, mode, handleViewAndPush, fromDoneButton, selectedOption, getButtonText, isHorizontalLayout }) => {
  const [pushPermissions, setPushPermissions] = useState({ can_push: false, has_mixed_stores: false });
  const [screenWidth, setScreenWidth] = useState(Dimensions.get('window').width);
  const route = useRoute();
  const dispatch = useDispatch();

  useEffect(() => {
    const onChange = ({ window }) => setScreenWidth(window.width);
    const subscription = Dimensions.addEventListener('change', onChange);
    return () => subscription?.remove?.();
  }, []);

  const fetchPushPermissions = async () => {
    try {
      const reportId = route.params?.id || route.params?.reportId;
      if (reportId) {
        const permissions = await dispatch(checkPushPermissions(reportId));
        setPushPermissions(permissions);
      } else {
        const permissions = await dispatch(checkPushPermissions());
        setPushPermissions(permissions);
      }
    } catch (error) {
      setPushPermissions({ can_push: false, has_mixed_stores: false });
    }
  };

  useEffect(() => {
    fetchPushPermissions();
  }, [route.params?.id, route.params?.reportId, dispatch]);

  useFocusEffect(
    React.useCallback(() => {
      fetchPushPermissions();
    }, [])
  );

  const isMobile = screenWidth < 768;
  const isSmallMobile = screenWidth < 480;
  const isLargeMobile = screenWidth >= 480 && screenWidth < 768;
  const isTablet = screenWidth >= 768 && screenWidth < 1024;
  const isNestHub = screenWidth >= 1000 && screenWidth <= 1024;

  const getResponsiveStyles = () => ({
    buttonContainer: {
      width: isHorizontalLayout ? 'auto' : '100%',
      maxWidth: isHorizontalLayout ? 'none' : (isMobile ? screenWidth - 20 : 600),
      margin: isHorizontalLayout ? 0 : 'auto',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: isHorizontalLayout ? 'flex-end' : 'space-between',
      alignItems: 'center',
      marginBottom: isNestHub ? 15 : isMobile ? 5 : 10,
      paddingHorizontal: isHorizontalLayout ? 0 : (isMobile ? 5 : 10),
      gap: isNestHub ? 12 : 8,
      zIndex: -1
    },
    button: {
      borderWidth: 1,
      borderColor: '#ffffff',
      borderRadius: 15,
      alignItems: 'center',
      justifyContent: 'center',
      height: isMobile ? 26 : 30,
      paddingHorizontal: 12,
      minWidth: 80,
      marginTop: 10,
    },
    buttonText: {
      color: '#ffffff',
      textAlign: 'center',
      fontFamily: fontFamily.font400,
      fontSize: isSmallMobile ? 12 : isMobile ? 14 : 16
    }
  });

  const styles = getResponsiveStyles();

  if (isCancelButtons && !fromPushInventory && !fromDoneButton) {
    return null;
  }

  if (fromPushInventory) {
    if (isSmallMobile && isHorizontalLayout) {
      return null;
    }

    return (
      <>
        {!isEditing ? (
          <View style={styles.buttonContainer}>
            <TouchableOpacity onPress={handlePushConfirm} style={[styles.button, styles.leftButton]}>
              <Text style={styles.buttonText}>
                {getButtonText ? getButtonText() : 'Push Inventory'}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={handleEdit} style={[styles.button, styles.leftButton]}>
              <Text style={styles.buttonText}>Edit</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View style={styles.buttonContainer}>
            <TouchableOpacity onPress={revertChange} style={[styles.button, styles.leftButton]}>
              <Text style={styles.buttonText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={handleSaveEdit} style={[styles.button, styles.rightButton]}>
              <Text style={styles.buttonText}>Save</Text>
            </TouchableOpacity>
          </View>
        )}
      </>
    );
  }

  if (fromDoneButton) {
    return (
      <>
        {!isEditing ? (
          <View style={styles.buttonContainer}>
            {pushPermissions.can_push ? (
              <>
                <TouchableOpacity onPress={handleConfirm} style={[styles.button, styles.leftButton]}>
                  <Text style={styles.buttonText}>Apply Record</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={handlePushConfirm} style={[styles.button, styles.leftButton]}>
                  <Text style={styles.buttonText}>Apply & Push</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={handleCancel} style={[styles.button, styles.centerButton]}>
                  <Text style={styles.buttonText}>Resume</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={handleEdit} style={[styles.button, styles.rightButton]}>
                  <Text style={styles.buttonText}>Edit</Text>
                </TouchableOpacity>
              </>
            ) : (
              <>
                <TouchableOpacity onPress={handleConfirm} style={[styles.button, styles.leftButton]}>
                  <Text style={styles.buttonText}>Apply</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={handleCancel} style={[styles.button, styles.centerButton]}>
                  <Text style={styles.buttonText}>Resume</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={handleEdit} style={[styles.button, styles.rightButton]}>
                  <Text style={styles.buttonText}>Edit</Text>
                </TouchableOpacity>
              </>
            )}
          </View>
        ) : (
          <View style={styles.buttonContainer}>
            <TouchableOpacity onPress={revertChange} style={[styles.button, styles.leftButton]}>
              <Text style={styles.buttonText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={handleSaveEdit} style={[styles.button, styles.rightButton]}>
              <Text style={styles.buttonText}>Save</Text>
            </TouchableOpacity>
          </View>
        )}
      </>
    );
  }

  return (
    <>
      {!isEditing ? (
        <View style={styles.buttonContainer}>
          <TouchableOpacity onPress={handleConfirm} style={[styles.button, styles.leftButton]}>
            <Text style={styles.buttonText}>Apply Record</Text>
          </TouchableOpacity>
          {pushPermissions.can_push && (
            <TouchableOpacity onPress={handlePushConfirm} style={[styles.button, styles.leftButton]}>
              <Text style={styles.buttonText}>Apply & Push</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity onPress={handleCancel} style={[styles.button, styles.centerButton]}>
            <Text style={styles.buttonText}>Resume</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={handleEdit} style={[styles.button, styles.rightButton]}>
            <Text style={styles.buttonText}>Edit</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <View style={styles.buttonContainer}>
          <TouchableOpacity onPress={revertChange} style={[styles.button, styles.leftButton]}>
            <Text style={styles.buttonText}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={handleSaveEdit} style={[styles.button, styles.rightButton]}>
            <Text style={styles.buttonText}>Save</Text>
          </TouchableOpacity>
        </View>
      )}
    </>
  );
};

const styles = {
  buttonContainer: {
    width: '100%',
    maxWidth: 600,
    margin: 'auto',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
    paddingHorizontal: 10
  },
  button: {
    borderWidth: 1,
    borderColor: '#ffffff',
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    height: 26,
    paddingHorizontal: 14,
    paddingVertical: 3,
    minWidth: 100,
    marginTop: 20,
  },
  leftButton: {
    alignSelf: 'flex-start',
  },
  centerButton: {
    alignSelf: 'center',
  },
  rightButton: {
    alignSelf: 'flex-end',
  },
  buttonText: {
    color: '#ffffff',
    textAlign: 'center',
    fontFamily: fontFamily.font400
  }
};

export default actionButtons;
